import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { pluginApi, serverApi } from '@/lib/api';
import { useApiMutation } from '@/hooks/useApi';
import { Loader2, Save, Lightbulb, ThumbsUp, ThumbsDown, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';

export function SuggestionsPlugin({ serverId }: { serverId: string }) {
  const { data: plugins } = useQuery({
    queryKey: ['servers', serverId, 'plugins'],
    queryFn: () => pluginApi.getPlugins(serverId),
  });

  const { data: channels = [] } = useQuery({
    queryKey: ['servers', serverId, 'channels'],
    queryFn: () => serverApi.getChannels(serverId),
  });

  const suggestionsPlugin = (plugins as any)?.find((p: any) => p.pluginId === 'suggestions');
  const isEnabled = suggestionsPlugin?.enabled ?? false;

  const { mutate: togglePlugin } = useApiMutation(
    ({ enabled }: { enabled: boolean }) => 
      enabled ? pluginApi.enablePlugin(serverId, 'suggestions') : pluginApi.disablePlugin(serverId, 'suggestions'),
    { invalidateQueries: [['servers', serverId, 'plugins']] }
  );

  const { mutate: updateConfig, isPending: saving } = useApiMutation(
    (config: any) => pluginApi.updatePluginConfig(serverId, 'suggestions', config),
    { 
      onSuccess: () => toast.success('Configuration saved!'),
      invalidateQueries: [['servers', serverId, 'plugins']] }
  );

  const [config, setConfig] = useState<any>({
    enabled: true,
    suggestionChannelId: '',
    reviewChannelId: '',
    allowAnonymous: true,
    requireApproval: false,
    autoThread: true,
    upvoteEmoji: '👍',
    downvoteEmoji: '👎',
    minimumVotes: 10,
    voteToImplement: 75
  });

  useEffect(() => {
    if (suggestionsPlugin?.config) {
      setConfig({ ...config, ...suggestionsPlugin.config });
    }
  }, [suggestionsPlugin]);

  const handleSave = () => {
    updateConfig(config);
  };

  if (!suggestionsPlugin) return null;

  return (
    <div className="space-y-6 max-w-4xl mx-auto pb-12">
      <div className="glass-card p-6 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-yellow-500/20 rounded-xl">
            <Lightbulb className="w-6 h-6 text-yellow-400" />
          </div>
          <div>
            <h3 className="font-bold text-xl">Suggestions & Voting</h3>
            <p className="text-sm text-muted-foreground">
              Let members suggest and vote on ideas.
            </p>
          </div>
        </div>
        <Switch
          checked={isEnabled}
          onCheckedChange={(enabled) => togglePlugin({ enabled })}
        />
      </div>

      {isEnabled && (
        <>
          {/* Channels */}
          <div className="glass-card p-6 space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Lightbulb className="w-5 h-5 text-yellow-400" />
              <h4 className="font-semibold">Channels</h4>
            </div>

            <div className="space-y-4">
              <div>
                <Label>Suggestion Channel</Label>
                <Select
                  value={config.suggestionChannelId || 'none'}
                  onValueChange={(value) => setConfig({ ...config, suggestionChannelId: value === 'none' ? '' : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona canale suggerimenti" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessun canale</SelectItem>
                    {(channels as any[]).map((channel: any) => (
                      <SelectItem key={channel.id} value={channel.id}>#{channel.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  Where approved suggestions will be posted.
                </p>
              </div>

              <div>
                <Label>Review Channel (Optional)</Label>
                <Select
                  value={config.reviewChannelId || 'none'}
                  onValueChange={(value) => setConfig({ ...config, reviewChannelId: value === 'none' ? '' : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona canale review" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessun canale</SelectItem>
                    {(channels as any[]).map((channel: any) => (
                      <SelectItem key={channel.id} value={channel.id}>#{channel.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  Where suggestions await staff approval (if enabled).
                </p>
              </div>
            </div>
          </div>

          {/* Voting Settings */}
          <div className="glass-card p-6 space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <ThumbsUp className="w-5 h-5 text-yellow-400" />
              <h4 className="font-semibold">Voting Settings</h4>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Upvote Emoji</Label>
                  <Input
                    placeholder="👍"
                    value={config.upvoteEmoji}
                    onChange={(e) => setConfig({ ...config, upvoteEmoji: e.target.value })}
                  />
                </div>

                <div>
                  <Label>Downvote Emoji</Label>
                  <Input
                    placeholder="👎"
                    value={config.downvoteEmoji}
                    onChange={(e) => setConfig({ ...config, downvoteEmoji: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Minimum Votes to Decide</Label>
                  <Input
                    type="number"
                    min="1"
                    value={config.minimumVotes}
                    onChange={(e) => setConfig({ ...config, minimumVotes: parseInt(e.target.value) })}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Total votes needed before auto-decision
                  </p>
                </div>

                <div>
                  <Label>% Upvotes to Implement</Label>
                  <Input
                    type="number"
                    min="50"
                    max="100"
                    value={config.voteToImplement}
                    onChange={(e) => setConfig({ ...config, voteToImplement: parseInt(e.target.value) })}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    % of upvotes needed to auto-approve
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Behavior */}
          <div className="glass-card p-6 space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle className="w-5 h-5 text-yellow-400" />
              <h4 className="font-semibold">Behavior</h4>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 px-4 bg-white/5 rounded-lg">
                <div>
                  <Label className="text-sm">Allow Anonymous Suggestions</Label>
                  <p className="text-xs text-muted-foreground">Hide author identity</p>
                </div>
                <Switch
                  checked={config.allowAnonymous}
                  onCheckedChange={(checked) => setConfig({ ...config, allowAnonymous: checked })}
                />
              </div>

              <div className="flex items-center justify-between py-2 px-4 bg-white/5 rounded-lg">
                <div>
                  <Label className="text-sm">Require Staff Approval</Label>
                  <p className="text-xs text-muted-foreground">Suggestions must be approved before posting</p>
                </div>
                <Switch
                  checked={config.requireApproval}
                  onCheckedChange={(checked) => setConfig({ ...config, requireApproval: checked })}
                />
              </div>

              <div className="flex items-center justify-between py-2 px-4 bg-white/5 rounded-lg">
                <div>
                  <Label className="text-sm">Auto-Create Threads</Label>
                  <p className="text-xs text-muted-foreground">Create discussion thread for each suggestion</p>
                </div>
                <Switch
                  checked={config.autoThread}
                  onCheckedChange={(checked) => setConfig({ ...config, autoThread: checked })}
                />
              </div>
            </div>
          </div>

          {/* Commands Info */}
          <div className="glass-card p-6 space-y-4">
            <h4 className="font-semibold mb-2">Available Commands</h4>
            <div className="space-y-2 text-sm">
              <div className="p-3 bg-white/5 rounded-lg">
                <code className="text-yellow-400">/suggest [idea]</code>
                <p className="text-muted-foreground mt-1">Submit a new suggestion</p>
              </div>
              <div className="p-3 bg-white/5 rounded-lg">
                <code className="text-yellow-400">/suggestions [status]</code>
                <p className="text-muted-foreground mt-1">View suggestions by status (pending/approved/rejected)</p>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end gap-3">
            <Button onClick={handleSave} disabled={saving} className="gap-2">
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save Configuration
                </>
              )}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
